import { createElement, Children, cloneElement, useRef, useEffect } from 'react';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { __rest, __assign } from 'tslib';
import { UitkPriceLockup, UitkPriceLockupAlign } from 'uitk-react-price-lockup';
import { UitkScrim, UitkScrimType, UitkScrimColorType } from 'uitk-react-scrim';
import { UitkText } from 'uitk-react-text';
import { classNames } from 'uitk-react-utils';

var UitkLayoutSpaceMap = [
    ['half', 'gap-half'],
    ['one', 'gap-one'],
    ['two', 'gap-two'],
    ['three', 'gap-three'],
    ['four', 'gap-four'],
    ['six', 'gap-six'],
    ['twelve', 'gap-twelve']
];
var UitkLayoutSpaces = new Map(UitkLayoutSpaceMap);
var UitkLayoutTagMap = [
    ['article', 'article'],
    ['aside', 'aside'],
    ['div', 'div'],
    ['footer', 'footer'],
    ['header', 'header'],
    ['section', 'section'],
    ['ul', 'ul'],
    ['ol', 'ol'],
    ['li', 'li']
];
var UitkLayoutTags = new Map(UitkLayoutTagMap);

var UitkLayout = function (props) {
    var className = props.className, tag = props.tag, attributes = __rest(props, ["className", "tag"]);
    // renders the custom Tag
    var Tag = ((tag && UitkLayoutTags.get(tag)) ||
        UitkLayoutTags.get('div'));
    return props.children ? (createElement(Tag, __assign({ className: className }, attributes), props.children)) : null;
};

var UitkLayoutGridColumnRowsMap = [
    [1, '1'],
    [2, '2'],
    [3, '3'],
    [4, '4'],
    [6, '6'],
    [8, '8'],
    [12, '12']
];
var UitkLayoutGridColumnRows = new Map(UitkLayoutGridColumnRowsMap);
var UitkLayoutGridSpanMap = [
    [1, '1'],
    [2, '2'],
    [3, '3'],
    [4, '4'],
    [5, '5'],
    [6, '6'],
    [7, '7'],
    [8, '8'],
    [9, '9'],
    [10, '10'],
    [11, '11'],
    [12, '12']
];
var UitkLayoutGridSpans = new Map(UitkLayoutGridSpanMap);
var UitkLayoutGridAlignMap = [
    ['start', 'start'],
    ['end', 'end'],
    ['center', 'center'],
    ['stretch', 'stretch']
];
var UitkLayoutGridAligns = new Map(UitkLayoutGridAlignMap);
var UitkLayoutGridPlaceMap = [
    ['start', 'start'],
    ['end', 'end'],
    ['center', 'center'],
    ['stretch', 'stretch'],
    ['space-around', 'space-around'],
    ['space-between', 'space-between'],
    ['space-evenly', 'space-evenly']
];
var UitkLayoutGridPlaces = new Map(UitkLayoutGridPlaceMap);
var UitkLayoutGridItemPlaceSelfMap = [
    ['start', 'start'],
    ['end', 'end'],
    ['center', 'center'],
    ['stretch', 'stretch']
];
var UitkLayoutGridItemPlaceSelfs = new Map(UitkLayoutGridItemPlaceSelfMap);
var UitkLayoutGridAutoFlowMap = [
    ['row', 'row'],
    ['column', 'column'],
    ['dense', 'dense'],
    ['row-dense', 'row-dense'],
    ['column-dense', 'column-dense']
];
var UitkLayoutGridAutoFlows = new Map(UitkLayoutGridAutoFlowMap);

var UitkLayoutGrid = function (props) {
    var alignContent = props.alignContent, alignItems = props.alignItems, autoFlow = props.autoFlow, space = props.space, columns = props.columns, className = props.className, justifyContent = props.justifyContent, justifyItems = props.justifyItems, rows = props.rows, tag = props.tag, dataTestId = props.dataTestId, attributes = __rest(props, ["alignContent", "alignItems", "autoFlow", "space", "columns", "className", "justifyContent", "justifyItems", "rows", "tag", "dataTestId"]);
    var baseClass = 'uitk-layout-grid';
    var resColumnsRowsClassStr = function (obj, classPrefix) {
        var str = Object.entries(obj)
            .map(function (_a) {
            var key = _a[0], val = _a[1];
            return baseClass + "-" + classPrefix + "-" + key + "-" + val;
        })
            .join(' ');
        return str;
    };
    var classNames = [
        baseClass,
        space && baseClass + '-' + UitkLayoutSpaces.get(space),
        alignContent &&
            baseClass + "-align-content-" + UitkLayoutGridPlaces.get(alignContent),
        alignItems &&
            baseClass + "-align-items-" + UitkLayoutGridAligns.get(alignItems),
        autoFlow &&
            baseClass + "-auto-flow-" + UitkLayoutGridAutoFlows.get(autoFlow),
        columns &&
            typeof columns === 'number' &&
            baseClass + "-columns-" + UitkLayoutGridColumnRows.get(columns),
        columns &&
            typeof columns === 'object' &&
            resColumnsRowsClassStr(columns, 'columns'),
        justifyContent &&
            baseClass + "-justify-content-" + UitkLayoutGridPlaces.get(justifyContent),
        justifyItems &&
            baseClass + "-justify-items-" + UitkLayoutGridAligns.get(justifyItems),
        rows &&
            typeof rows === 'number' &&
            baseClass + "-rows-" + UitkLayoutGridColumnRows.get(rows),
        rows && typeof rows === 'object' && resColumnsRowsClassStr(rows, 'rows'),
        className
    ].filter(function (style) { return typeof style === 'string'; });
    return (createElement(UitkLayout, __assign({ className: classNames.join(' '), tag: tag }, attributes, dataTestId && { 'data-test-id': dataTestId }), props.children || null));
};

var UitkLayoutGridItem = function (props) {
    var alignSelf = props.alignSelf, className = props.className, colEnd = props.colEnd, colSpan = props.colSpan, colStart = props.colStart, justifySelf = props.justifySelf, rowEnd = props.rowEnd, rowSpan = props.rowSpan, rowStart = props.rowStart;
    var childrenArray = Children.toArray(props.children);
    if (!childrenArray.length) {
        return null;
    }
    var firstChild = childrenArray[0];
    var firstChildClassName = firstChild.props.className;
    var baseClass = 'uitk-layout-grid-item';
    var resSpanClassStr = function (obj, classPrefix) {
        var str = Object.entries(obj)
            .map(function (_a) {
            var key = _a[0], val = _a[1];
            return baseClass + "-" + classPrefix + "-" + key + "-" + val;
        })
            .join(' ');
        return str;
    };
    var classNames = [
        baseClass,
        firstChildClassName,
        alignSelf &&
            baseClass + "-align-self-" + UitkLayoutGridItemPlaceSelfs.get(alignSelf),
        colEnd &&
            typeof colEnd === 'number' &&
            baseClass + "-columnend-" + UitkLayoutGridSpans.get(colEnd),
        colEnd && typeof colEnd === 'string' && baseClass + "-columnend-" + colEnd,
        colEnd &&
            typeof colEnd === 'object' &&
            resSpanClassStr(colEnd, 'columnend'),
        colSpan &&
            typeof colSpan === 'number' &&
            baseClass + "-columnspan-" + UitkLayoutGridSpans.get(colSpan),
        colSpan &&
            typeof colSpan === 'string' &&
            baseClass + "-columnspan-" + colSpan,
        colSpan &&
            typeof colSpan === 'object' &&
            resSpanClassStr(colSpan, 'columnspan'),
        colStart &&
            typeof colStart === 'number' &&
            baseClass + "-columnstart-" + UitkLayoutGridSpans.get(colStart),
        colStart &&
            typeof colStart === 'string' &&
            baseClass + "-columnstart-" + colEnd,
        colStart &&
            typeof colStart === 'object' &&
            resSpanClassStr(colStart, 'columnstart'),
        justifySelf &&
            baseClass + "-justify-self-" + UitkLayoutGridItemPlaceSelfs.get(justifySelf),
        rowEnd &&
            typeof rowEnd === 'number' &&
            baseClass + "-rowend-" + UitkLayoutGridSpans.get(rowEnd),
        rowEnd && typeof rowEnd === 'string' && baseClass + "-rowend-" + rowEnd,
        rowEnd && typeof rowEnd === 'object' && resSpanClassStr(rowEnd, 'rowend'),
        rowSpan &&
            typeof rowSpan === 'number' &&
            baseClass + "-rowspan-" + UitkLayoutGridSpans.get(rowSpan),
        rowSpan && typeof rowSpan === 'string' && baseClass + "-rowspan-" + rowSpan,
        rowSpan &&
            typeof rowSpan === 'object' &&
            resSpanClassStr(rowSpan, 'rowspan'),
        rowStart &&
            typeof rowStart === 'number' &&
            baseClass + "-rowstart-" + UitkLayoutGridSpans.get(rowStart),
        rowStart &&
            typeof rowStart === 'string' &&
            baseClass + "-rowstart-" + rowStart,
        rowStart &&
            typeof rowStart === 'object' &&
            resSpanClassStr(rowStart, 'rowstart'),
        className
    ].filter(function (style) { return typeof style === 'string'; });
    var decoratedChild = cloneElement(firstChild, {
        className: classNames.join(' ')
    });
    return decoratedChild;
};

var UitkStepIndicatorExperimental = function (_a) {
    var playBack = _a.playBack, changeLinkClickCallback = _a.changeLinkClickCallback, className = _a.className;
    var baseClassName = 'uitk-step-indicator';
    var stepClassName = baseClassName + "-step";
    var stepIndicatorRef = useRef(null);
    var currentStepRef = useRef(null);
    var resizeTimeout;
    var componentClassNames = classNames(baseClassName, 'no-bullet', 'unfade-right');
    var delimeterClasses = classNames(baseClassName + "-details-texts", 'all-l-padding-one', 'all-r-padding-one');
    var steps = playBack.steps, priceLockup = playBack.priceLockup;
    var buttonScroll = function (parent) {
        if (parent.currentTarget) {
            applyFade(parent.currentTarget);
        }
    };
    var applyFade = function (target) {
        var _a, _b, _c, _d, _e, _f, _g;
        var maxScrollLeft = target.scrollWidth - target.clientWidth;
        if (target.scrollLeft >= maxScrollLeft - 10) {
            (_a = target.classList).add.apply(_a, ['unfade-right']);
            (_b = target.classList).remove.apply(_b, ['fade-right']);
        }
        else {
            (_c = target.classList).add.apply(_c, ['fade-right']);
            (_d = target.classList).remove.apply(_d, ['unfade-right']);
        }
        if (target.scrollLeft <= 10) {
            (_e = target.classList).remove.apply(_e, ['fade-left']);
            (_f = target.classList).remove.apply(_f, ['fade-right']);
        }
        else {
            (_g = target.classList).add.apply(_g, ['fade-left']);
        }
    };
    var onWindowResize = function () {
        if (!resizeTimeout) {
            resizeTimeout = setTimeout(function () {
                resizeTimeout = null;
                applyFade(stepIndicatorRef.current);
            }, 200);
        }
    };
    var scrollTo = function (ref) {
        if (ref) {
            try {
                ref.scrollIntoView({
                    behavior: 'auto',
                    block: 'nearest',
                    inline: 'center'
                });
            }
            catch (error) {
                return;
            }
        }
    };
    var getChangeLink = function (path, changeLinkLabel, changeLinkA11yLabel, analytics, index) {
        var handleOnClick = function (event) {
            var referrerId = analytics && analytics.referrerId;
            var linkName = analytics && analytics.linkName;
            changeLinkClickCallback(referrerId, linkName, event, index);
        };
        if (path && changeLinkLabel) {
            return (createElement("a", { href: path, onClick: handleOnClick, "aria-label": changeLinkA11yLabel },
                createElement(UitkText, { size: 200 }, changeLinkLabel)));
        }
        else {
            return null;
        }
    };
    var getPlaybackLabel = function (label, index) {
        if (label.origin !== undefined) {
            var simpleLabel = label;
            var labelId = simpleLabel.icon.id + "-" + index;
            return (createElement("span", null,
                createElement("span", { className: baseClassName + "-provider-limit" }, simpleLabel.text),
                createElement("span", { className: delimeterClasses }, "\u00B7"),
                createElement("span", { className: baseClassName + "-details-texts" },
                    simpleLabel.origin,
                    createElement(UitkIcon, { id: labelId, name: simpleLabel.icon.id, title: simpleLabel.icon.description, size: UitkIconSize.EXTRA_SMALL, className: "all-b-padding-half" }),
                    simpleLabel.destination)));
        }
        else {
            var simpleLabel = label;
            return (createElement("span", null,
                createElement("span", { className: simpleLabel.detailText ? baseClassName + "-provider-limit" : '' }, simpleLabel.text),
                simpleLabel.detailText && (createElement("span", { className: delimeterClasses }, "\u00B7")),
                simpleLabel.detailText && (createElement("span", { className: baseClassName + "-details-texts" }, simpleLabel.detailText))));
        }
    };
    useEffect(function () {
        applyFade(stepIndicatorRef.current);
        window.addEventListener('resize', onWindowResize);
        scrollTo(currentStepRef.current);
        window.scrollTo(0, 0);
        return function () {
            window.removeEventListener('resize', onWindowResize);
        };
    }, []);
    return (createElement(UitkLayoutGrid, { columns: 8, space: "four", alignContent: "center", dataTestId: "step-indicator", className: className },
        createElement(UitkLayoutGridItem, { colSpan: priceLockup ? { small: 6, medium: 6, extra_large: 6 } : 8 },
            createElement("div", { className: baseClassName + "-container" },
                createElement("ul", { ref: stepIndicatorRef, role: "list", className: componentClassNames, onScroll: buttonScroll }, steps.map(function (step, index) {
                    var stepName = stepClassName + "-" + index;
                    var label = step.label, a11yLabel = step.a11yLabel, currentStep = step.currentStep, changeSelection = step.changeSelection;
                    var action = changeSelection && changeSelection.action;
                    var changeLinkLabel = changeSelection && changeSelection.label;
                    var changeLinkA11yLabel = changeSelection && changeSelection.a11yLabel;
                    var analytics = action && action.analytics;
                    var resource = action && action.resource;
                    var path = resource && resource.relativePath;
                    return (createElement("li", { className: baseClassName + "-step-wrapper", role: "listitem", key: stepName, ref: currentStep ? currentStepRef : undefined, "aria-label": a11yLabel },
                        createElement(UitkText, { size: 300, align: "left", theme: currentStep ? 'primary' : undefined, weight: currentStep ? 'bold' : 'regular', className: baseClassName + "-step-details-wrapper" },
                            getPlaybackLabel(label, index),
                            getChangeLink(path, changeLinkLabel, changeLinkA11yLabel, analytics, index)),
                        index < steps.length - 1 && (createElement(UitkIcon, { name: "chevron_right", size: UitkIconSize.SMALL, className: "all-l-padding-two all-r-padding-two" }))));
                })),
                createElement("div", { className: baseClassName + "-fade-left" },
                    createElement(UitkScrim, { type: UitkScrimType.LEFT, color: UitkScrimColorType.NEUTRAL })),
                createElement("div", { className: baseClassName + "-fade-right" },
                    createElement(UitkScrim, { type: UitkScrimType.RIGHT, color: UitkScrimColorType.NEUTRAL })))),
        priceLockup && (createElement(UitkLayoutGridItem, { colSpan: { small: 2, medium: 2, extra_large: 2 } },
            createElement(UitkPriceLockup, { align: UitkPriceLockupAlign.RIGHT, lockupPrice: priceLockup.lockupPrice || undefined, priceSubtextStandard: priceLockup.priceSubtextStandard || undefined, a11yPrice: priceLockup.a11yPrice, strikeThroughPrice: priceLockup.strikeThroughPrice || undefined })))));
};

export { UitkStepIndicatorExperimental };
